import {useState, useEffect} from 'react'
import {useCustomQuery} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useRealTimeInventory from './use-realtime-inventory'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../../../utils/site-config'

/**
 * Hook to fetch product recommendations.
 * @param {string} skus - Product SKU(s) to fetch similars for
 * @param {Object} options - Options
 * @param {boolean} options.enabled - Whether to fetch (default: true). Use false for lazy loading.
 */
const useProductsSimilars = (skus, options = {}) => {
    const {enabled: enabledOption = true} = options
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    const [isLoading, setIsLoading] = useState(false)
    const [productIds, setProductIds] = useState('')
    const isVickyAIEnabled = getSiteEnvVarValue(appConfig, 'VICKY_ENABLED', siteId) === 'true'

    const {refetch, data: recommendations} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    siteId: site.id,
                    c_sku: skus,
                    c_brand: 'GRG'
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/similars',
                    apiName: 'vickyai'
                }
            },
            rawResponse: false
        },
        {
            // Only fetch when VickyAI is enabled AND enabledOption is true (e.g., modal is open)
            enabled: isVickyAIEnabled && enabledOption,
            onSuccess: (result) => {
                if (Array.isArray(result?.data)) {
                    result.data.reduce((result, item) => {
                        const key = item.id
                        result[key] = item
                        return result
                    }, {})

                    const ids = result.data.map((item) => item.id).join(', ') || ''
                    setProductIds(ids)
                }

                setIsLoading(false)
                return result
            }
        }
    )

    useEffect(() => {
        // Only refetch when enabled AND skus change
        if (skus && enabledOption && isVickyAIEnabled) {
            setIsLoading(true);
            refetch();
        }
    }, [skus, enabledOption, isVickyAIEnabled])

    useEffect(() => {
        if (enabledOption && isVickyAIEnabled) {
            setIsLoading(true)
        }
    }, [site, locale, enabledOption, isVickyAIEnabled])

    const {availableSkus} = useRealTimeInventory(productIds)

    if (recommendations?.data && Object.keys(recommendations?.data).length > 0) {
        recommendations?.data?.filter((product) => availableSkus?.productIDs?.includes(product.id))
    }

    return {
        isLoading,
        recommendations,
        productIds,
        refetch
    }
}

export {useProductsSimilars}
